import paths, { LoginQueryParams } from '@constants/paths';

/**
 * Maps protected route prefixes to their login redirect parameters.
 */
export const PROTECTED_ROUTE_PREFIXES: ReadonlyArray<readonly [string, LoginQueryParams]> = [
  [paths.MY_ACCOUNT, LoginQueryParams.MY_ACCOUNT],
  [paths.MY_LISTS, LoginQueryParams.MY_LISTS],
  [paths.ACCOUNT_ORDERS, LoginQueryParams.ACCOUNT_ORDERS],
  [paths.MY_COMMON_ITEMS, LoginQueryParams.MY_COMMON_ITEMS],
] as const;

/**
 * Routes that render the login page or modal.
 * Route must be listed here — see app/pageClientRouteCoverage.spec.ts.
 */
export const LOGIN_PAGE_ROUTES = [paths.USER_LOGIN, paths.B2B_LOGIN];

/**
 * Login flows where the user should stay on the page they came from instead of being redirected after login:
 * the cart page, and saving the cart or a product to a list.
 */
const NO_REDIRECT_LOGIN_PARAMS: ReadonlyArray<LoginQueryParams> = [
  LoginQueryParams.CART_PAGE,
  LoginQueryParams.CART,
  LoginQueryParams.ADD_TO_LIST,
];

/**
 * Maps login flow parameters to their redirect destinations.
 * These are not protected routes, but indicate where the user should go after login.
 */
const LOGIN_FLOW_REDIRECTS: Partial<Record<LoginQueryParams, string>> = {
  [LoginQueryParams.CHECKOUT_PAGE]: paths.CHECKOUT,
  [LoginQueryParams.CREATE_ACCOUNT]: paths.MY_ACCOUNT,
};

/**
 * Returns the login parameter for the protected route a path belongs to, or undefined if it isn't a protected route.
 * Used to pick the login screen message when `from` is a raw path.
 */
export const getLoginParamFromPathname = (pathname: string): LoginQueryParams | undefined =>
  PROTECTED_ROUTE_PREFIXES.find(([prefix]) => pathname.startsWith(prefix))?.[1];

/**
 * Returns the path for a given login redirect parameter.
 * Falls back to MY_ACCOUNT path if param doesn't match any protected route.
 */
const getPathFromLoginParam = (param: LoginQueryParams): string => {
  const flowRedirect = LOGIN_FLOW_REDIRECTS[param];
  if (flowRedirect) {
    return flowRedirect;
  }
  for (const [prefix, routeParam] of PROTECTED_ROUTE_PREFIXES) {
    if (routeParam === param) {
      return prefix;
    }
  }
  return paths.MY_ACCOUNT;
};

/**
 * Resolves the `from` login query param to a path. Accepts either a raw path or a LoginQueryParams value.
 */
const resolveRedirectPath = (from: string): string =>
  from.startsWith('/') ? from : getPathFromLoginParam(from as LoginQueryParams);

/**
 * Returns where the user should go after logging in with the given `from` param,
 * or null when they should stay on the page they came from.
 */
export const getPostLoginRedirectPath = (from: LoginQueryParams | null | undefined): string | null => {
  if (!from || NO_REDIRECT_LOGIN_PARAMS.includes(from)) return null;
  return resolveRedirectPath(from);
};
